import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { FlaskConical, CheckCircle, FileText, Route, Loader2 } from 'lucide-react';
import type { PayerWizardProgress, RuleReviewSummary } from '@/lib/payer-wizard/types';
import { LANE_DISPLAY, type LaneType } from '@/lib/payer-wizard/laneTypes';

interface TestDrivePanelProps {
  progress: PayerWizardProgress;
  ruleReview: Record<string, RuleReviewSummary>;
}

interface TestResult {
  authRequired: 'YES' | 'NO' | 'CONDITIONAL';
  requiredDocs: string[];
  enabledLanes: LaneType[];
  lanePriority: LaneType[];
  slaHours: number;
  followUpHours: number;
}

const LATERALITY_OPTIONS = [
  { value: 'LEFT', label: 'Left' },
  { value: 'RIGHT', label: 'Right' },
  { value: 'BILATERAL', label: 'Bilateral' },
  { value: 'N/A', label: 'N/A' },
];

const SITE_OF_SERVICE_OPTIONS = [
  { value: 'INPATIENT', label: 'Inpatient' },
  { value: 'OUTPATIENT', label: 'Outpatient' },
  { value: 'ASC', label: 'ASC' },
];

const INJURY_TYPE_OPTIONS = [
  { value: 'ACUTE', label: 'Acute' },
  { value: 'CHRONIC', label: 'Chronic' },
  { value: 'TRAUMATIC', label: 'Traumatic' },
];

export function TestDrivePanel({ progress, ruleReview }: TestDrivePanelProps) {
  const [cptCode, setCptCode] = useState('27447');
  const [laterality, setLaterality] = useState('LEFT');
  const [siteOfService, setSiteOfService] = useState('OUTPATIENT');
  const [injuryType, setInjuryType] = useState('CHRONIC');
  const [selectedPlanIndex, setSelectedPlanIndex] = useState(0);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<TestResult | null>(null);
  
  const handleRunTest = async () => {
    setRunning(true);
    
    // Simulate processing delay
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Get the plan and lane config
    const plan = progress.plans[selectedPlanIndex];
    const laneConfig = progress.plan_lane_configs[selectedPlanIndex];
    const summary = plan ? ruleReview[plan.ruleset_source] : null;
    
    // Generate test result based on configuration
    const testResult: TestResult = {
      authRequired: 'CONDITIONAL',
      requiredDocs: summary?.doc_requirements || ['H&P', 'Imaging'],
      enabledLanes: laneConfig ? 
        (Object.entries(laneConfig.lanes_enabled)
          .filter(([_, enabled]) => enabled)
          .map(([lane]) => lane as LaneType)) : [],
      lanePriority: laneConfig?.lane_priority || [],
      slaHours: summary?.sla_hours || 336,
      followUpHours: summary?.follow_up_cadence_hours || 72,
    };
    
    // Determine auth based on CPT
    const cptNum = parseInt(cptCode);
    if (cptNum >= 27000 && cptNum <= 27899) {
      testResult.authRequired = 'CONDITIONAL';
    } else if (cptNum >= 99201 && cptNum <= 99215) {
      testResult.authRequired = 'NO';
    }
    
    // Add bilateral modifier doc requirement
    if (laterality === 'BILATERAL') {
      testResult.requiredDocs = [...testResult.requiredDocs, 'Bilateral Justification'];
    }
    
    setResult(testResult);
    setRunning(false);
  };
  
  return (
    <Card className="border-dashed border-2">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <FlaskConical className="h-4 w-4 text-primary" />
          Test Drive (Dry Run)
        </CardTitle>
        <CardDescription className="text-xs">
          Preview auth/doc requirements for a sample case. No records created.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">CPT Code</Label>
            <Input
              value={cptCode}
              onChange={(e) => setCptCode(e.target.value)}
              placeholder="27447"
              className="h-8 text-sm"
            />
          </div>
          
          <div className="space-y-1">
            <Label className="text-xs">Laterality</Label>
            <Select value={laterality} onValueChange={setLaterality}>
              <SelectTrigger className="h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LATERALITY_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-1">
            <Label className="text-xs">Site of Service</Label>
            <Select value={siteOfService} onValueChange={setSiteOfService}>
              <SelectTrigger className="h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SITE_OF_SERVICE_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-1">
            <Label className="text-xs">Injury Type</Label>
            <Select value={injuryType} onValueChange={setInjuryType}>
              <SelectTrigger className="h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {INJURY_TYPE_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {progress.plans.length > 1 && (
          <div className="space-y-1">
            <Label className="text-xs">Test Against Plan</Label>
            <Select 
              value={String(selectedPlanIndex)} 
              onValueChange={(v) => setSelectedPlanIndex(parseInt(v))}
            >
              <SelectTrigger className="h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {progress.plans.map((plan, idx) => (
                  <SelectItem key={idx} value={String(idx)}>{plan.plan_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        
        <Button 
          onClick={handleRunTest} 
          disabled={running || !cptCode}
          size="sm"
          className="w-full"
        >
          {running ? (
            <>
              <Loader2 className="mr-2 h-3 w-3 animate-spin" />
              Running...
            </>
          ) : (
            <>
              <FlaskConical className="mr-2 h-3 w-3" />
              Run Test Case
            </>
          )}
        </Button>
        
        {result && (
          <>
            <Separator />
            <div className="space-y-3">
              <h4 className="text-sm font-medium">Preview Result</h4>
              
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Auth Required</Label>
                  <Badge 
                    variant="outline"
                    className={
                      result.authRequired === 'YES' 
                        ? 'border-destructive text-destructive' 
                        : result.authRequired === 'CONDITIONAL'
                          ? 'border-amber-500 text-amber-600'
                          : 'border-primary text-primary'
                    }
                  >
                    {result.authRequired}
                  </Badge>
                </div>
                
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">SLA</Label>
                  <span>{result.slaHours}h ({Math.round(result.slaHours / 24)}d)</span>
                </div>
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground flex items-center gap-1">
                  <FileText className="h-3 w-3" />
                  Required Documents
                </Label>
                <div className="flex flex-wrap gap-1">
                  {result.requiredDocs.map((doc, i) => (
                    <Badge key={i} variant="secondary" className="text-xs">
                      {doc}
                    </Badge>
                  ))}
                </div>
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground flex items-center gap-1">
                  <Route className="h-3 w-3" />
                  Lanes (Priority Order)
                </Label>
                <div className="flex flex-wrap gap-1">
                  {result.lanePriority.map((lane, i) => (
                    <Badge key={lane} variant="outline" className="text-xs">
                      {i + 1}. {LANE_DISPLAY[lane].label.split(' ')[0]}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
